import { Entity } from 'engine/Entity';
import { V2 } from 'engine/lib/V2';

export enum ModuleType {
  Cpu = 'Cpu',
  Use = 'Use',
  Nearby = 'Nearby',
  Inventory = 'Inventory',
  Logic = 'Logic',
  MoveTo = 'MoveTo',
  Follow = 'Follow',
  Scanner = 'Scanner',
  Door = 'Door',
}

export const MODULE_UNIT_SIZE = 200;

export const ModuleSize: Record<ModuleType, V2> = {
  [ModuleType.Cpu]: [1, 3],
  [ModuleType.Use]: [1, 3],
  [ModuleType.Nearby]: [1, 3],
  [ModuleType.Inventory]: [1.5, 3],
  [ModuleType.Logic]: [1, 3],
  [ModuleType.MoveTo]: [2, 3],
  [ModuleType.Follow]: [1.5, 3],
  [ModuleType.Scanner]: [2.5, 3],
  [ModuleType.Door]: [2, 3],
};

export interface WireSource {
  moduleId: number,
  socketName: string,
}

export interface ModuleBase<T, TInputs extends string, TConfig = null> {
  type: T,
  id: number,
  position: V2,
  inputs: Partial<Record<TInputs, WireSource>>,
  config: TConfig,
}

export type AnyModule = ModuleBase<ModuleType, string, unknown>;

export interface CpuModule extends ModuleBase<ModuleType.Cpu, 'cpu'> {
}

export enum BotToolChoice {
  WateringCan = 'WateringCan',
  Axe = 'Axe',
  Laser = 'Laser',
}

export const WATERING_CAN_MAX = 10;

export type UseModule = ModuleBase<ModuleType.Use, 'target', {
  tool: null | BotToolChoice, // null = 'auto'
}>;

export enum NearbySelection {
  DryPlant = 'DryPlant',
  Water = 'Water',
  Tree = 'Tree',
  Human = 'Human',
  Bot = 'Bot',
}

export type NearbyModule = ModuleBase<ModuleType.Nearby, 'item', {
  manualSelection: null | NearbySelection,
}>;

export enum InventoryOperation {
  Lacks = 'Lacks',
  Has = 'Has',
}

export enum InventorySelection {
  Water = 'Water',
  RedHerring = 'RedHerring',
}

export type InventoryModule = ModuleBase<ModuleType.Inventory, never, {
  operation: InventoryOperation,
  selection: null | InventorySelection,
}>;

export interface LogicModule extends ModuleBase<ModuleType.Logic, '0' | '1' | '2' | '3' | '4'> {
  operation: 'fallback' | 'sequence',
}

export type MoveToModule = ModuleBase<ModuleType.MoveTo, never, {
  target: V2,
}>;

export type FollowModule = ModuleBase<ModuleType.Follow, 'target'>;

export type ScannerModule = ModuleBase<ModuleType.Scanner, never>;

export type DoorModule = ModuleBase<ModuleType.Door, 'pwr'>;

export type Module =
  | CpuModule
  | UseModule
  | NearbyModule
  | InventoryModule
  | LogicModule
  | MoveToModule
  | FollowModule
  | ScannerModule
  | DoorModule;

export type ModuleMap = {
  [T in ModuleType]: Extract<Module, { type: T }>;
};

// TODO: Probably need to use entityId instead of Entity to make this serializable
export type ProcessPayload =
  | { type: 'command', action: 'use', tool: BotToolChoice, target: Entity, enRoute: boolean, dest: V2 }
  | { type: 'command', action: 'move', target: V2 }
  | { type: 'command', action: 'follow', target: Entity }
  | { type: 'command', action: 'open' }
  | { type: 'item', item: InventoryItem }
  | { type: 'entity', entity: Entity };

interface MissingError {
  type: 'missing',
  item: BotToolChoice | InventorySelection,
}

interface UnclearError {
  type: 'unclear',
}

export type ProcessError = MissingError | UnclearError/* | OtherError */;

export type ProcessStatus =
  | { status: 'null' }
  | { status: 'running', value: ProcessPayload }
  | { status: 'error', error: ProcessError }
  | { status: 'success', moduleId: number, value: ProcessPayload };

export interface InventoryItem {
  type: BotToolChoice,
  count: number,
}

type CacheResult = {
  result: ProcessStatus,
  inputs: Partial<{ [socketName: string]: CacheResult }>,
  config: unknown,
};

export interface BotPalette {
  eyes: string,
  body: string,
  stroke: string,
}

export interface BotState {
  id: string,
  position: V2,
  botSpeed: number,
  running: boolean,
  looping: boolean,
  modules: Module[],
  cache: Partial<{ [moduleId: string]: CacheResult }>,
  inventory: InventoryItem[],
  palette: BotPalette,
  hp: number,
  didDie: boolean,
  electrifiedEnd: number | null,
}

export enum SocketType {
  Item = 0b001,
  Instance = 0b010,
  Command = 0b100,
}

const AnySocketType: SocketType = SocketType.Item | SocketType.Instance | SocketType.Command;

export interface SocketDef {
  type: SocketType,
  direction: 'in' | 'out',
  position: V2,
}

const type = <T>(t: T): T => t;

export const socketDefs = {
  [ModuleType.Cpu]: {
    cpu: type<SocketDef>({
      type: SocketType.Command,
      direction: 'in',
      position: [100, 300],
    }),
  },
  [ModuleType.Use]: {
    target: type<SocketDef>({
      type: SocketType.Instance,
      direction: 'in',
      position: [100, 440],
    }),
    cmd: type<SocketDef>({
      type: SocketType.Command,
      direction: 'out',
      position: [100, 560],
    }),
  },
  [ModuleType.Nearby]: {
    item: type<SocketDef>({
      type: SocketType.Item,
      direction: 'in',
      position: [60, 180],
    }),
    found: type<SocketDef>({
      type: SocketType.Instance,
      direction: 'out',
      position: [100, 560],
    }),
  },
  [ModuleType.Inventory]: {
    item: type<SocketDef>({
      type: SocketType.Item,
      direction: 'out',
      position: [200, 560],
    }),
  },
  [ModuleType.Logic]: {
    0: type<SocketDef>({
      type: AnySocketType,
      direction: 'in',
      position: [100, 150],
    }),
    1: type<SocketDef>({
      type: AnySocketType,
      direction: 'in',
      position: [100, 220],
    }),
    2: type<SocketDef>({
      type: AnySocketType,
      direction: 'in',
      position: [100, 290],
    }),
    3: type<SocketDef>({
      type: AnySocketType,
      direction: 'in',
      position: [100, 360],
    }),
    4: type<SocketDef>({
      type: AnySocketType,
      direction: 'in',
      position: [100, 430],
    }),
    out: type<SocketDef>({
      type: AnySocketType,
      direction: 'out',
      position: [100, 560],
    }),
  },
  [ModuleType.MoveTo]: {
    cmd: type<SocketDef>({
      type: SocketType.Command,
      direction: 'out',
      position: [300, 550],
    }),
  },
  [ModuleType.Follow]: {
    target: type<SocketDef>({
      type: SocketType.Instance,
      direction: 'in',
      position: [150, 300],
    }),
    cmd: type<SocketDef>({
      type: SocketType.Command,
      direction: 'out',
      position: [150, 420],
    }),
  },
  [ModuleType.Scanner]: {
    accepted: type<SocketDef>({
      type: SocketType.Instance,
      direction: 'out',
      position: [430, 520],
    }),
  },
  [ModuleType.Door]: {
    pwr: type<SocketDef>({
      type: SocketType.Instance,
      direction: 'in',
      position: [70, 520],
    }),
    open: type<SocketDef>({
      type: SocketType.Command,
      direction: 'out',
      position: [330, 520],
    }),
  },
};

export const determineNearbySelection = (module: Module, bot: BotState): NearbySelection | null => {
  switch (module.type) {
    case ModuleType.Nearby: {
      if (module.inputs.item) {
        const itemModule = bot.modules.find(m => m.id === module.inputs.item?.moduleId);
        if (itemModule) return determineNearbySelection(itemModule, bot);
      }
      return module.config.manualSelection;
    }
    case ModuleType.Inventory: {
      switch (module.config.selection) {
        case InventorySelection.RedHerring: return null;
        case InventorySelection.Water: return NearbySelection.Water;
        case null: return null;
      }
    }
    // eslint-disable-next-line no-fallthrough
    case ModuleType.Logic: {
      const result = bot.cache[module.id]?.result;
      if (result?.status === 'success') {
        const sourceModule = bot.modules.find(m => m.id === result.moduleId);
        if (sourceModule) return determineNearbySelection(sourceModule, bot);
      }
    }
  }
  return null;
};

export const determineTool = (module: Module, bot: BotState): BotToolChoice | null => {
  switch (module.type) {
    case ModuleType.Use: {
      const targetModuleId = module.inputs.target?.moduleId;
      const targetModule = targetModuleId && bot.modules.find(m => m.id === targetModuleId);
      if (!targetModule) return null;
      return determineTool(targetModule, bot);
    }
    case ModuleType.Nearby: {
      const selection = determineNearbySelection(module, bot);
      switch (selection) {
        case NearbySelection.DryPlant: return BotToolChoice.WateringCan;
        case NearbySelection.Water: return BotToolChoice.WateringCan;
        case NearbySelection.Tree: return BotToolChoice.Axe;
      }
      return null;
    }
    case ModuleType.Logic: {
      const result = bot.cache[module.id]?.result;
      if (result?.status === 'success') {
        const sourceModule = bot.modules.find(m => m.id === result.moduleId);
        if (sourceModule) return determineTool(sourceModule, bot);
      }
    }
  }
  return null;
};

export const emptyBotState: BotState = {
  id: '',
  position: [0, 0],
  botSpeed: 0,
  running: false,
  looping: false,
  modules: [],
  cache: {},
  inventory: [],
  palette: { eyes: '', body: '', stroke: '' },
  hp: 0,
  didDie: false,
  electrifiedEnd: null,
};
